import React from 'react';
import { createPortal } from 'react-dom';

/**
 * ConfirmDialog — a small themed modal used before every destructive
 * action in the app (cancel, reschedule, mark completed, mark no-show).
 *
 * Design notes:
 *   • Portalled to <body> with a high z-index so it always sits above
 *     the diary, the detail panel, and any other stacking context.
 *   • Default focus is on the CANCEL button (the safe side) — the
 *     destructive action must never fire on a stray Enter key press.
 *   • Escape and backdrop click both cancel.
 *   • Body text is caller-supplied per booking so the sentence can
 *     name the client, time, service and consequence (spec).
 *
 * Public API:
 *   Use the `useConfirm()` hook — it returns [dialogNode, confirm()],
 *   where `confirm({...})` returns a Promise resolving to true/false.
 */

export const ConfirmDialog = ({
  open,
  title,
  body,
  danger = false,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onCancel,
  onConfirm,
  busy = false,
}) => {
  const cancelBtnRef = React.useRef(null);

  // Focus the CANCEL button on open. Never focus the destructive action.
  React.useEffect(() => {
    if (open && cancelBtnRef.current) {
      // Next tick — after the portal has mounted.
      const t = setTimeout(() => cancelBtnRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Escape cancels. Enter is NOT bound to confirm — it targets the
  // focused button (which is Cancel by default).
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (!busy) onCancel?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  return createPortal(
    <div
      className="confirm-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      data-testid="confirm-dialog"
      onClick={(e) => {
        // Backdrop click cancels; clicks INSIDE the dialog card don't bubble.
        if (e.target === e.currentTarget && !busy) onCancel?.();
      }}
    >
      <div className="confirm-card">
        <h3 id="confirm-title" className="confirm-title" data-testid="confirm-title">{title}</h3>
        {body && <p className="confirm-body" data-testid="confirm-body">{body}</p>}
        <div className="confirm-actions">
          <button
            ref={cancelBtnRef}
            type="button"
            className="btn ghost"
            onClick={onCancel}
            disabled={busy}
            data-testid="confirm-cancel"
          >{cancelLabel}</button>
          <button
            type="button"
            className={`btn ${danger ? 'danger' : 'primary'}`}
            onClick={onConfirm}
            disabled={busy}
            data-testid="confirm-ok"
          >{busy ? 'Working…' : confirmLabel}</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

/**
 * Hook: returns `[dialogNode, confirm]`.
 * Usage:
 *   const [confirmNode, confirm] = useConfirm();
 *   const ok = await confirm({ title, body, danger: true, confirmLabel: 'Cancel booking' });
 *   if (!ok) return;
 *   ... run destructive action ...
 */
export const useConfirm = () => {
  const [state, setState] = React.useState({ open: false });
  const resolverRef = React.useRef(null);

  const confirm = React.useCallback((opts) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({ open: true, ...opts, busy: false });
    });
  }, []);

  const close = React.useCallback((value) => {
    setState((s) => ({ ...s, open: false }));
    resolverRef.current?.(value);
    resolverRef.current = null;
  }, []);

  const node = (
    <ConfirmDialog
      {...state}
      onCancel={() => close(false)}
      onConfirm={() => close(true)}
    />
  );

  return [node, confirm];
};
